(() => {
  const STORAGE_KEY = 'sass-upsell-redirect-state';
  const DEFAULT_TTL_MS = 24 * 60 * 60 * 1000;

  /**
   * Builds the storage key used to group redirect state for a particular source and first-upsell page pair.
   * @param {string} sourcePageUrl - The page that initiated the upsell flow.
   * @param {string} firstUpsellPageUrl - The first upsell page that should receive the redirect.
   * @returns {string} A normalized composite key for storage lookups.
   */
  function getStateKey(sourcePageUrl, firstUpsellPageUrl) {
    return `${sourcePageUrl}::${firstUpsellPageUrl}`;
  }

  /**
   * Normalizes a URL to an absolute href using the current origin as the base.
   * @param {string} url - The URL to normalize.
   * @returns {string} The normalized absolute URL, or an empty string if parsing fails.
   */
  function normalizeUrl(url) {
    try {
      return new URL(url, window.location.origin).href;
    } catch {
      return '';
    }
  }

  /**
   * Builds a comparable URL that ignores query strings but preserves the path and hash.
   * @param {string} url - The URL to compare.
   * @returns {string} A simplified URL string for matching current and stored pages.
   */
  function getComparableUrl(url) {
    try {
      const parsedUrl = new URL(url, window.location.origin);
      return `${parsedUrl.origin}${parsedUrl.pathname}${parsedUrl.hash}`;
    } catch {
      return '';
    }
  }

  /**
   * Reads the raw redirect-state payload from localStorage.
   * @returns {object|null} The parsed storage payload, or null when it is missing or invalid.
   */
  function getRawState() {
    try {
      const rawState = window.localStorage.getItem(STORAGE_KEY);

      if (!rawState) return null;

      return JSON.parse(rawState);
    } catch {
      return null;
    }
  }

  /**
   * Returns the stored entries map from the current nested storage format.
   * @returns {Object<string, object>} The stored entry map keyed by state key.
   */
  function getEntriesState() {
    const rawState = getRawState();

    if (!rawState || !rawState.entries || typeof rawState.entries !== 'object') {
      return {};
    }

    return rawState.entries;
  }

  /**
   * Persists the current entry map back to localStorage.
   * @param {Object<string, object>} entries - The entry map to store.
   */
  function persistEntriesState(entries) {
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          entries,
        }),
      );
    } catch {
      // Ignore storage failures so the redirect flow still completes.
    }
  }

  /**
   * Removes the redirect state from localStorage.
   */
  function clear() {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Ignore storage failures so reads remain non-blocking.
    }
  }

  /**
   * Validates and normalizes a stored redirect entry, clearing invalid or expired data.
   * @param {object|null} parsedState - A potential stored redirect state.
   * @returns {object|null} A normalized redirect state, or null when the data is invalid or expired.
   */
  function normalizeStoredState(parsedState) {
    if (!parsedState) {
      return null;
    }

    const redirectUrl = normalizeUrl(parsedState?.redirectUrl);
    const sourcePageUrl = normalizeUrl(parsedState?.sourcePageUrl);
    const firstUpsellPageUrl = normalizeUrl(parsedState?.firstUpsellPageUrl);
    const createdAt = Number(parsedState?.createdAt);
    const expiresAt = Number(parsedState?.expiresAt);

    if (!redirectUrl || !sourcePageUrl || !firstUpsellPageUrl || Number.isNaN(createdAt)) {
      clear();
      return null;
    }

    const resolvedExpiresAt = Number.isNaN(expiresAt) ? createdAt + DEFAULT_TTL_MS : expiresAt;

    if (Date.now() > resolvedExpiresAt) {
      clear();
      return null;
    }

    return {
      redirectUrl,
      sourcePageUrl,
      firstUpsellPageUrl,
      createdAt,
      expiresAt: resolvedExpiresAt,
    };
  }

  /**
   * Reads the stored redirect state for a specific source and first-upsell page combination.
   * @param {string} sourcePageUrl - The initiating source page.
   * @param {string} firstUpsellPageUrl - The first upsell page to look up.
   * @returns {object|null} The matching redirect state, or null when no valid entry exists.
   */
  function readForEntry(sourcePageUrl, firstUpsellPageUrl) {
    const normalizedSourcePageUrl = normalizeUrl(sourcePageUrl);
    const normalizedFirstUpsellPageUrl = normalizeUrl(firstUpsellPageUrl);

    if (!normalizedSourcePageUrl || !normalizedFirstUpsellPageUrl) {
      return null;
    }

    const stateKey = getStateKey(normalizedSourcePageUrl, normalizedFirstUpsellPageUrl);
    const entries = getEntriesState();
    const normalizedState = normalizeStoredState(entries[stateKey]);

    if (!normalizedState) {
      if (entries[stateKey]) {
        delete entries[stateKey];
        persistEntriesState(entries);
      }
      return null;
    }

    return normalizedState;
  }

  /**
   * Finds the most recent valid redirect state for the current page.
   * @param {string} currentPageUrl - The current browser URL.
   * @returns {object|null} The matching stored redirect state, if any.
   */
  function readForCurrentPage(currentPageUrl) {
    const normalizedCurrentPageUrl = normalizeUrl(currentPageUrl);
    if (!normalizedCurrentPageUrl) {
      return null;
    }

    const currentPageComparableUrl = getComparableUrl(normalizedCurrentPageUrl);
    const entries = getEntriesState();
    let matchingState = null;

    Object.entries(entries).forEach(([, entry]) => {
      const normalizedState = normalizeStoredState(entry);
      if (!normalizedState) {
        return;
      }

      const firstUpsellPageUrl = normalizeUrl(normalizedState.firstUpsellPageUrl);
      if (!firstUpsellPageUrl) {
        return;
      }

      const firstUpsellComparableUrl = getComparableUrl(firstUpsellPageUrl);
      const isMatchingPage =
        firstUpsellComparableUrl === currentPageComparableUrl || firstUpsellPageUrl === normalizedCurrentPageUrl;

      if (isMatchingPage) {
        if (!matchingState || Number(normalizedState.createdAt) > Number(matchingState.createdAt)) {
          matchingState = normalizedState;
        }
      }
    });

    return matchingState;
  }

  /**
   * Stores a redirect state entry for a specific source and first-upsell page flow.
   * @param {object} nextState - The redirect state payload to persist.
   * @param {string} nextState.sourcePageUrl - The initiating source page URL.
   * @param {string} nextState.firstUpsellPageUrl - The first upsell page URL.
   * @param {string} nextState.redirectUrl - The URL to redirect to after the flow advances.
   * @param {number} [nextState.ttlMs] - Optional TTL in milliseconds for the stored state.
   */
  function write(nextState) {
    const normalizedSourcePageUrl = normalizeUrl(nextState?.sourcePageUrl);
    const normalizedFirstUpsellPageUrl = normalizeUrl(nextState?.firstUpsellPageUrl);
    const normalizedRedirectUrl = normalizeUrl(nextState?.redirectUrl);
    const ttlMs = Number(nextState?.ttlMs);
    const resolvedTtlMs = Number.isNaN(ttlMs) || ttlMs <= 0 ? DEFAULT_TTL_MS : ttlMs;

    if (!normalizedRedirectUrl || !normalizedSourcePageUrl || !normalizedFirstUpsellPageUrl) return;

    try {
      const createdAt = Date.now();
      const stateKey = getStateKey(normalizedSourcePageUrl, normalizedFirstUpsellPageUrl);
      const entries = getEntriesState();

      entries[stateKey] = {
        redirectUrl: normalizedRedirectUrl,
        sourcePageUrl: normalizedSourcePageUrl,
        firstUpsellPageUrl: normalizedFirstUpsellPageUrl,
        createdAt,
        expiresAt: createdAt + resolvedTtlMs,
      };

      persistEntriesState(entries);
    } catch {
      // Ignore storage failures so the redirect flow still completes.
    }
  }

  window.sassUpsellRedirectState = {
    clear,
    getComparableUrl,
    normalizeUrl,
    readForCurrentPage,
    readForEntry,
    write,
  };
})();
